import { ROUTER_BASENAME } from '../api';
import { BOOKING_BRAND } from '../components/booking/bookingUi';

const MANIFEST_LINK_ID = 'booking-pwa-manifest';
const THEME_META_ID = 'booking-pwa-theme-color';
const APPLE_TITLE_ID = 'booking-pwa-apple-title';

/** @returns {string} e.g. "" or "/workwisesoft" */
function appBasePath() {
  if (ROUTER_BASENAME) return ROUTER_BASENAME;
  if (typeof window === 'undefined') return '';
  if (window.location.pathname.startsWith('/workwisesoft')) return '/workwisesoft';
  return process.env.PUBLIC_URL?.replace(/\/$/, '') || '';
}

export function bookingRoutePath() {
  return `${appBasePath()}/public/booking`;
}

export function bookingSwUrl() {
  return `${appBasePath()}/public/booking/sw.js`;
}

export function bookingSwScope() {
  return `${bookingRoutePath()}/`;
}

function originRoot() {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${appBasePath()}`;
}

export function buildBookingManifest() {
  const startUrl = typeof window !== 'undefined' ? `${window.location.origin}${bookingRoutePath()}` : bookingRoutePath();
  const root = originRoot();
  return {
    id: 'workwisesoft-booking',
    name: 'Book a Collection',
    short_name: 'Book',
    description: 'Book a waste collection in London. No online payment required.',
    start_url: startUrl,
    scope: bookingSwScope(),
    display: 'standalone',
    display_override: ['standalone', 'minimal-ui'],
    orientation: 'portrait-primary',
    theme_color: BOOKING_BRAND.primary,
    background_color: BOOKING_BRAND.bg,
    categories: ['business', 'utilities'],
    lang: 'en-GB',
    dir: 'ltr',
    icons: [
      {
        src: `${root}/logo192.png`,
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any',
      },
      {
        src: `${root}/logo512.png`,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any',
      },
      {
        src: `${root}/logo512.png`,
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
      },
    ],
  };
}

let manifestBlobUrl = null;
let swRegistration = null;

function upsertLink(id, rel, href, extra = {}) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('link');
    el.id = id;
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  Object.entries(extra).forEach(([k, v]) => {
    el.setAttribute(k, v);
  });
  return el;
}

function upsertMeta(id, name, content) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('meta');
    el.id = id;
    if (name.startsWith('apple-')) {
      el.setAttribute('name', name);
    } else {
      el.name = name;
    }
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return el;
}

/**
 * Enable booking-only PWA (manifest + scoped SW + iOS meta).
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function enableBookingPwa() {
  if (typeof window === 'undefined') return null;

  const manifest = buildBookingManifest();
  if (manifestBlobUrl) URL.revokeObjectURL(manifestBlobUrl);
  manifestBlobUrl = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/json' }));
  upsertLink(MANIFEST_LINK_ID, 'manifest', manifestBlobUrl);

  const officeManifest = document.querySelector('link[rel="manifest"]:not([id])');
  if (officeManifest) {
    officeManifest.setAttribute('data-booking-hidden', 'true');
    officeManifest.setAttribute('disabled', 'true');
    officeManifest.media = 'not all';
  }

  upsertMeta(THEME_META_ID, 'theme-color', BOOKING_BRAND.primary);
  upsertMeta('booking-pwa-apple-capable', 'apple-mobile-web-app-capable', 'yes');
  upsertMeta('booking-pwa-apple-status', 'apple-mobile-web-app-status-bar-style', 'default');
  upsertMeta(APPLE_TITLE_ID, 'apple-mobile-web-app-title', 'Book');

  const root = originRoot();
  upsertLink('booking-pwa-apple-touch', 'apple-touch-icon', `${root}/logo192.png`);

  if (!('serviceWorker' in navigator)) return null;

  try {
    swRegistration = await navigator.serviceWorker.register(bookingSwUrl(), {
      scope: bookingSwScope(),
      updateViaCache: 'none',
    });
    return swRegistration;
  } catch (e) {
    console.warn('[booking-pwa] Service worker registration failed', e);
    return null;
  }
}

export function disableBookingPwa() {
  if (typeof window === 'undefined') return;

  const link = document.getElementById(MANIFEST_LINK_ID);
  if (link) link.remove();
  if (manifestBlobUrl) {
    URL.revokeObjectURL(manifestBlobUrl);
    manifestBlobUrl = null;
  }

  const officeManifest = document.querySelector('link[rel="manifest"][data-booking-hidden]');
  if (officeManifest) {
    officeManifest.removeAttribute('disabled');
    officeManifest.removeAttribute('data-booking-hidden');
    officeManifest.media = '';
  }

  ['booking-pwa-theme-color', 'booking-pwa-apple-capable', 'booking-pwa-apple-status', APPLE_TITLE_ID].forEach(
    (id) => {
      document.getElementById(id)?.remove();
    }
  );
  document.getElementById('booking-pwa-apple-touch')?.remove();

  if (swRegistration) {
    swRegistration.unregister().catch(() => {});
    swRegistration = null;
  }
}

export function isStandaloneDisplay() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}
